import { type FileData, FileIconMap } from "@qendo/profile/files";
import * as Sharing from "expo-sharing";
import { useState } from "react";
import { ActivityIndicator, View } from "react-native";
import { Button } from "~/components/Button";
import { ImageBox } from "~/components/layout/gallery/ImageBox";
import { ListingItem, type ListingItemProps } from "~/components/layout/ListingItem";
import { Padding } from "~/constants/layout";
import { useFeedback } from "~/providers/FeedbackProvider";
import { useGallery } from "~/providers/GalleryProvider";
import { isDocument, isImage } from "~/util/qendo/media";

export type FileGalleryListingItemProps = {
    item: FileData;
    document?: string;
} & Partial<ListingItemProps>;

export const FileGalleryListingItem = ({ item, document, ...itemProps }: FileGalleryListingItemProps) => {
    const { downloadDocument } = useGallery();
    const { addToast } = useFeedback();
    const [preview, setPreview] = useState(false);
    const [downloading, setDownloading] = useState(false);

    const can_preview = isImage(item);
    const is_document = isDocument(item);
    const doDownload = () => {
        if (downloading || !is_document) {
            return;
        }

        setDownloading(true);

        downloadDocument(document, item.id)
            .then(async (file) => {
                if (file?.exists) {
                    addToast({ content: "File downloaded." });

                    if (await Sharing.isAvailableAsync()) {
                        await Sharing.shareAsync(file.uri, {
                            mimeType: file.type,
                            UTI: file.type,
                        });
                    }
                }
            })
            .catch(() => {
                addToast({ icon: "warning-outline", content: "Could not download file." });
            })
            .finally(() => setDownloading(false));
    };

    return (
        <>
            <ListingItem
                {...itemProps}
                title={item.name}
                icon={FileIconMap[item.category]}
                hideChevron
                subtitle={is_document && item.file_size}
                right={() => (
                    <>
                        {is_document &&
                            (downloading ? (
                                <View style={[Padding.sm]}>
                                    <ActivityIndicator size={20} />
                                </View>
                            ) : (
                                <Button
                                    icon="file"
                                    variant="neutral-quiet"
                                    size="sm"
                                    onPress={doDownload}
                                    loading={downloading}
                                />
                            ))}
                        {can_preview && (
                            <Button
                                icon="view-on"
                                variant="neutral-quiet"
                                size="sm"
                                onPress={() => setPreview(can_preview)}
                            />
                        )}
                    </>
                )}
            />
            {can_preview && preview && <ImageBox image={item} onRequestClose={() => setPreview(false)} />}
        </>
    );
};
